// BulkIdCards.jsx
// Section-wise + Excel bulk Student ID Card generator.
//   • From Section: pick Academic Year → Branch → Class → Section; photos are
//     pulled straight from the backend student record (no upload needed).
//   • From Excel: download the sample sheet, fill it, upload it back. Photos come
//     from an optional photo_url column, else are matched to a backend student by
//     admission_no, else fall back to the placeholder silhouette.
//   Output: one multi-page PDF (CR80 54×86mm, front+back per student) or a batch
//   Print, both reusing the exact same card renderer as the single-card flow.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { renderCertificateForStudent, inlineImagesForCapture } from './DynamicCertificateBuilder';

// ─── Helpers (mirrors of the single-card flow) ─────────────────────────────────

const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));

// Shared localStorage key for the principal signature. Uploaded here on the
// student ID-card page and persisted so the Employee ID card can default to the
// same signature (EmployeeIdCard.jsx reads the same key).
export const ID_CARD_PRINCIPAL_SIG_KEY = 'rizee_idcard_principal_sig';

// Map any raw cert-type string to a canonical filter key (we only need IDCARD here).
const toFilterKey = (raw) => {
  const t = String(raw || '').toLowerCase().replace(/[\s_-]/g, '');
  if (t === 'idcard' || t.includes('idcard')) return 'IDCARD';
  return String(raw || '').toUpperCase();
};

// Normalise a profile-photo value into a usable <img src> (same rules as CreateCertifictae1).
const normalizePhotoSrc = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  if (s.startsWith('data:') || /^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  if (/^\/9j\//.test(s))                       return `data:image/jpeg;base64,${s}`;
  if (/^iVBORw0KGgo/.test(s))                  return `data:image/png;base64,${s}`;
  if (/^R0lGOD/.test(s))                       return `data:image/gif;base64,${s}`;
  if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(s))   return `data:image/jpeg;base64,${s}`;
  return s;
};

const photoOf = (student) => normalizePhotoSrc(student.profile_image_base || student.profile_image);

// Build the ID-card data object for a student straight from the student row.
// The Green-Wave card only needs Class/Section, Admission No, Father Name, DOB,
// Phone + photo — all present on the student-list row, so no extra fetch.
const buildStudentData = (student, className, sectionName) => {
  const d = {};
  const set = (k, v) => { if (v) d[k] = v; };
  set('student_name',  student.student_name);
  set('roll_no',       student.roll_no);
  set('admission_no',  student.admission_no);
  set('dob',           student.dob);
  set('class_name',    className || student.class_name);
  set('section_name',  sectionName || student.section_name);
  set('father_name',   student.father_name || student.parent_name);
  set('mother_name',   student.mother_name);
  set('phone',         student.student_mobile || student.parent_mobile);
  set('pen_no',        student.pen_no);
  set('aadhar_no',     student.adhar_no);
  set('admitted_date', student.admitted_date);
  set('student_photo', photoOf(student));
  return d;
};

// Rasterise one card element (the 54×86mm card div) to a PNG data URL. Squares the
// corners / drops the shadow for full-bleed, and (front only) pins the green body
// to its laid-out height so html2canvas doesn't leave a white gap at the bottom —
// same fixes as the single-card renderIdCardImages().
const captureCardEl = async (el, isFront) => {
  const prevRadius = el.style.borderRadius;
  const prevShadow = el.style.boxShadow;
  el.style.borderRadius = '0';
  el.style.boxShadow = 'none';

  let bodyEl, prevFlex, prevH;
  if (isFront && el.children[1]) {
    bodyEl = el.children[1];
    prevFlex = bodyEl.style.flex;
    prevH = bodyEl.style.height;
    bodyEl.style.flex = 'none';
    bodyEl.style.height = bodyEl.offsetHeight + 'px';
  }

  const restore = await inlineImagesForCapture(el);
  let url = '';
  try {
    const canvas = await html2canvas(el, { scale: 4, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
    url = canvas.toDataURL('image/png');
  } finally {
    restore();
    el.style.borderRadius = prevRadius;
    el.style.boxShadow = prevShadow;
    if (bodyEl) { bodyEl.style.flex = prevFlex; bodyEl.style.height = prevH; }
  }
  return url;
};

// ─── Multi-select dropdown with checkboxes (matches the Dashboard tab style) ───
const PURPLE = '#6366f1';

function MultiSelect({ label, icon, options, selected, onChange, onToggleOpen, isOpen, required = false, idKey = 'value', labelKey = 'label' }) {
  const containerRef = useRef(null);
  const [openAbove, setOpenAbove] = useState(false);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenAbove(spaceAbove > spaceBelow && spaceBelow < 240);
    }
  }, [isOpen]);

  const allSelected  = options.length > 0 && selected.length === options.length;
  const noneSelected = selected.length === 0;
  const toggleAll    = () => onChange(allSelected ? [] : options.map(o => String(o[idKey])));
  const toggleOne    = (id) => { const sid = String(id); onChange(selected.includes(sid) ? selected.filter(s => s !== sid) : [...selected, sid]); };
  const displayLabel = (noneSelected || allSelected) ? `All ${label}s` : `${selected.length} selected`;
  const dropdownPos  = openAbove
    ? { bottom: '100%', top: 'auto', marginTop: 0, marginBottom: 4 }
    : { top: '100%', bottom: 'auto', marginTop: 4, marginBottom: 0 };

  return (
    <div ref={containerRef} style={{ position: 'relative', zIndex: isOpen ? 200 : 1 }}>
      <div style={ms.label}>{icon && <i className={icon} style={{ marginRight: 5 }} />}{label}{required && <span style={{ color: '#ef4444' }}> *</span>}</div>
      <button type="button" onClick={onToggleOpen}
        style={{ ...ms.trigger, borderColor: isOpen ? PURPLE : '#d1d5db', boxShadow: isOpen ? `0 0 0 3px ${PURPLE}22` : 'none' }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }}>{displayLabel}</span>
        {!noneSelected && !allSelected && (
          <span style={{ background: PURPLE, color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{selected.length}</span>
        )}
        <i className={`fal fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }} />
      </button>
      {isOpen && (
        <div style={{ ...ms.dropdown, ...dropdownPos }}>
          <label style={ms.checkRow}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={ms.checkbox} />
            <span style={{ ...ms.checkLabel, fontWeight: 700, color: '#374151' }}>Select All</span>
          </label>
          <div style={{ height: 1, background: '#e5e7eb', margin: '2px 0' }} />
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {options.length === 0
              ? <div style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>No options</div>
              : options.map(opt => {
                  const sid = String(opt[idKey]); const checked = selected.includes(sid);
                  return (
                    <label key={sid} style={{ ...ms.checkRow, background: checked ? `${PURPLE}08` : 'transparent' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(opt[idKey])} style={ms.checkbox} />
                      <span style={{ ...ms.checkLabel, color: checked ? PURPLE : '#374151', fontWeight: checked ? 600 : 400 }}>{opt[labelKey]}</span>
                    </label>
                  );
                })
            }
          </div>
        </div>
      )}
    </div>
  );
}

const ms = {
  label:     { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 },
  trigger:   { width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' },
  dropdown:  { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden' },
  checkRow:  { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', margin: 0, width: '100%' },
  checkbox:  { width: 16, height: 16, accentColor: PURPLE, cursor: 'pointer', flexShrink: 0 },
  checkLabel:{ fontSize: 13, userSelect: 'none' },
};

// ─── Component ─────────────────────────────────────────────────────────────────

const BulkIdCards = ({
  branches = [], allClasses = [], streams = [], students = [],
  savedTemplates = [], academicYears = [], certificateTypes = [],
  onGenerateCertificate = null,
}) => {
  const captureRootRef = useRef(null);
  const filterRef      = useRef(null);

  const [openDropdown, setOpenDropdown] = useState(null); // 'class' | 'section' | null

  const [selAcademicYear, setSelAcademicYear] = useState('');
  const [selClasses,  setSelClasses]  = useState([]);   // multi — class ids; [] = all
  const [selSections, setSelSections] = useState([]);   // multi — section names; [] = all
  const [selTemplateId, setSelTemplateId] = useState('');
  const [excludedIds, setExcludedIds] = useState(() => new Set()); // students opted out
  const [principalSig, setPrincipalSig] = useState(() => {         // optional, applied to all
    // Reuse the last signature uploaded here so it also defaults onto the Employee card.
    try { return localStorage.getItem(ID_CARD_PRINCIPAL_SIG_KEY) || ''; } catch { return ''; }
  });
  const [busy, setBusy]       = useState(false);
  const [progress, setProgress] = useState(null);                  // { done, total }
  const [toast, setToast]     = useState('');
  const [generated, setGenerated] = useState(false);               // cards captured & ready
  const [genImages, setGenImages] = useState([]);                  // [{ name, img }] front+back
  const [showView, setShowView]   = useState(false);               // full preview modal

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  // ── Dropdown derivation ────────────────────────────────────────────────────
  // There is no Branch dropdown: `branches` arrives already narrowed to the
  // header's (NavbarOne) selection, and every option below spans all of them.
  const branchIdSet = useMemo(() => new Set(branches.map(b => String(b.id))), [branches]);

  const sourceSections = useMemo(
    () => branches.flatMap(b => b.sections || []),
    [branches]
  );

  const classOpts = useMemo(() => {
    if (!branches.length) return [];
    const ids = new Set(sourceSections.map(s => String(s.class_id)));
    return allClasses.filter(c => ids.has(String(c.id))).sort((a, b) => Number(a.id) - Number(b.id));
  }, [branches, sourceSections, allClasses]);

  // Section options scoped to the chosen classes (or all classes of the branch).
  const sectionOpts = useMemo(() => {
    const classSet = new Set(selClasses.map(String));
    const filtered = sourceSections
      .filter(s => classSet.size === 0 || classSet.has(String(s.class_id)));
    const seen = new Set();
    const unique = [];
    filtered.forEach(s => { if (s.section_name && !seen.has(s.section_name)) { seen.add(s.section_name); unique.push(s); } });
    return unique.sort((a, b) => a.section_name.localeCompare(b.section_name));
  }, [sourceSections, selClasses]);

  // react-select option lists.
  const classSelectOpts   = useMemo(() => classOpts.map(c => ({ value: String(c.id), label: c.class })), [classOpts]);
  const sectionSelectOpts = useMemo(() => sectionOpts.map(s => ({ value: s.section_name, label: s.section_name })), [sectionOpts]);

  // Stable keys so the "select all by default" effects only run when the actual
  // set of options changes (branch switch / data load), not on every render.
  const classOptsKey   = useMemo(() => classOpts.map(c => c.id).join(','), [classOpts]);
  const sectionOptsKey = useMemo(() => sectionOpts.map(s => s.section_name).join(','), [sectionOpts]);

  // Close any open multi-select when clicking outside the filter row.
  useEffect(() => {
    const onDown = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setOpenDropdown(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Persist the principal signature so the Employee ID card defaults to the same one.
  useEffect(() => {
    try {
      if (principalSig) localStorage.setItem(ID_CARD_PRINCIPAL_SIG_KEY, principalSig);
      else localStorage.removeItem(ID_CARD_PRINCIPAL_SIG_KEY);
    } catch { /* storage unavailable / quota — non-fatal */ }
  }, [principalSig]);

  // ── Defaults ────────────────────────────────────────────────────────────────
  // Latest academic year (academicYears arrive sorted newest-first).
  useEffect(() => {
    if (!selAcademicYear && academicYears.length) setSelAcademicYear(String(academicYears[0].id));
  }, [academicYears, selAcademicYear]);

  // All classes selected by default whenever the class option set changes.
  useEffect(() => {
    setSelClasses(classOpts.map(c => String(c.id)));
  }, [classOptsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Section starts empty (shows the "All Sections" label) — the user picks which
  // sections they want. Cleared when the option set changes (e.g. branch switch).
  useEffect(() => {
    setSelSections([]);
  }, [sectionOptsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Students across every branch in the header's scope, narrowed by the
  // multi-selected classes/sections.
  const sectionStudents = useMemo(() => {
    if (!branchIdSet.size) return [];
    const classSet   = new Set(selClasses.map(String));
    const sectionSet = new Set(selSections);
    return students
      .filter(s =>
        (!selAcademicYear || String(s.academic_year_id) === selAcademicYear) &&
        branchIdSet.has(String(s.branch_id)) &&
        (classSet.size === 0   || classSet.has(String(s.class_id))) &&
        (sectionSet.size === 0 || sectionSet.has(String(s.section_name)))
      )
      .sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
  }, [students, selAcademicYear, branchIdSet, selClasses, selSections]);

  const sourceStudents = sectionStudents;

  // ── ID-card templates ──────────────────────────────────────────────────────
  const idCardTemplates = useMemo(() => {
    return savedTemplates
      .map(t => {
        let certType = '';
        try { certType = JSON.parse(t.template)?.certType || ''; } catch {}
        return { ...t, _filterKey: toFilterKey(certType || '') };
      })
      .filter(t => t._filterKey === 'IDCARD');
  }, [savedTemplates]);

  const selTemplate = useMemo(() => {
    if (!idCardTemplates.length) return null;
    if (selTemplateId) {
      const found = idCardTemplates.find(t => String(t.id) === String(selTemplateId));
      if (found) return found;
    }
    // Prefer a template belonging to a branch that is in scope.
    const branchMatch = idCardTemplates.find(t => branchIdSet.has(String(t.branch_id)));
    if (branchMatch) return branchMatch;
    return idCardTemplates[0];
  }, [idCardTemplates, selTemplateId, branchIdSet]);

  const cfg = useMemo(() => {
    if (!selTemplate) return null;
    try { return JSON.parse(selTemplate.template); } catch { return null; }
  }, [selTemplate]);

  const hiddenKeys = useMemo(() => {
    if (!cfg?.fields) return [];
    return cfg.fields.filter(f => !f.visible).map(f => f.key);
  }, [cfg]);

  // Used only to name the download batch. With several branches in scope there is
  // no single name, so fall back to a generic label.
  const branchName = useMemo(
    () => (branches.length === 1 ? (branches[0].branch_name || '') : ''),
    [branches]
  );

  // Students actually included (not opted out).
  const includedStudents = useMemo(
    () => sourceStudents.filter(s => !excludedIds.has(String(s.student_id))),
    [sourceStudents, excludedIds]
  );

  const sigImages = principalSig ? { 0: principalSig } : {};

  const toggleStudent = (id) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAll   = () => setExcludedIds(new Set());
  const deselectAll = () => setExcludedIds(new Set(sourceStudents.map(s => String(s.student_id))));

  const resetSelection = () => {
    // Year falls back to its default via the effects; classes/sections re-fill to
    // "all" automatically when the option set is recomputed. Branch is not reset
    // here — it belongs to the header now.
    setSelAcademicYear('');
    setSelClasses([]); setSelSections([]); setSelTemplateId('');
    setExcludedIds(new Set());
  };

  // ── Each included student rendered (hidden, full-size) for capture & preview ─
  // Each student carries its own class_name / section_name, so pass empty here.
  const renderedCards = useMemo(() => {
    if (!cfg) return [];
    return includedStudents.map(s => ({
      student: s,
      html: renderCertificateForStudent(cfg, buildStudentData(s, '', ''), hiddenKeys, sigImages),
    }));
  }, [cfg, includedStudents, hiddenKeys, principalSig]);

  // Any change to the rendered set invalidates a previous generation — the user
  // must press Generate again so the exported cards always match what's on screen.
  useEffect(() => { setGenerated(false); setGenImages([]); }, [renderedCards]);

  const batchName = `ID_Cards_${branchName || 'Branch'}`.replace(/[^a-zA-Z0-9_-]+/g, '_');

  // All selection fields are mandatory before generating. Section is not selected
  // by default, so the user must pick at least one section.
  const validate = () => {
    const missing = [];
    if (!selAcademicYear)    missing.push('Academic Year');
    if (!selClasses.length)  missing.push('Class');
    if (!selSections.length) missing.push('Section');
    if (!selTemplate)        missing.push('ID Card Template');
    return missing;
  };

  // ── Generate: rasterise every card (front+back) ONCE (reused for PDF / Image /
  //    Print), then save each card to the backend so it shows in the Dashboard. ─
  const handleGenerate = async () => {
    const root = captureRootRef.current;
    if (!root) return;

    const missing = validate();
    if (missing.length) { showToast(`Please select: ${missing.join(', ')}`); return; }
    if (!includedStudents.length) { showToast('No students selected to generate.'); return; }

    setBusy(true);
    setProgress({ done: 0, total: includedStudents.length });
    try {
      const wrappers = root.querySelectorAll('[data-bulk-card]');
      const imgs = [];
      const perStudent = []; // { student, frontImg } — for the backend save
      for (let i = 0; i < wrappers.length; i++) {
        const wrap    = wrappers[i];
        const student = includedStudents[i] || {};
        const base = [student.admission_no, student.student_name].filter(Boolean).join('_')
          .replace(/[^a-zA-Z0-9_-]+/g, '_') || `card_${i + 1}`;
        const front = wrap.querySelector('#dcb-idcard-front');
        const back  = wrap.querySelector('#dcb-idcard-back');
        const sides = [
          front ? { el: front.firstElementChild || front, isFront: true,  side: 'front' } : null,
          back  ? { el: back.firstElementChild  || back,  isFront: false, side: 'back'  } : null,
        ].filter(Boolean);

        let frontImg = null;
        for (const s of sides) {
          const img = await captureCardEl(s.el, s.isFront);
          if (s.isFront) frontImg = img;
          const suffix = sides.length > 1 ? `_${s.side}` : '';
          imgs.push({ name: `${base}${suffix}`, img });
        }

        // Capture the FULL card area (front + back together, on its grey backdrop)
        // for the dashboard record — this matches the single-student certificate
        // save, so the saved image looks identical in the Dashboard view.
        let saveImg = frontImg;
        const restoreWrap = await inlineImagesForCapture(wrap);
        try {
          const fc = await html2canvas(wrap, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
          saveImg = fc.toDataURL('image/png');
        } catch (e) { /* fall back to the front image */ } finally { restoreWrap(); }

        perStudent.push({ student, saveImg });
        setProgress({ done: i + 1, total: wrappers.length });
      }
      setGenImages(imgs);
      setGenerated(true);

      // ── Save each card to the backend (Dashboard / certificate history table) ──
      if (onGenerateCertificate) {
        const certTypeId = selTemplate.certificate_type_id
          || certificateTypes.find(ct => /id\s*card/i.test(ct.certificate_type))?.id;
        const issueDate = new Date().toISOString().split('T')[0];
        setProgress({ done: 0, total: perStudent.length });
        let saved = 0;
        for (let i = 0; i < perStudent.length; i++) {
          const { student, saveImg } = perStudent[i];
          try {
            await onGenerateCertificate({
              edu_student_id:          student.student_id,
              certificate_type_id:     certTypeId,
              certificate_no:          `IDC-${student.student_id}-${Date.now()}`,
              issued_date:             issueDate,
              certificate_file_base64: saveImg,
            });
            saved++;
          } catch (e) {
            console.warn('[BulkIdCards] save failed for', student.student_id, e?.message || e);
          }
          setProgress({ done: i + 1, total: perStudent.length });
        }
        showToast(`Generated & saved ${saved} of ${perStudent.length} ID card${perStudent.length !== 1 ? 's' : ''} to the dashboard.`);
      } else {
        showToast(`Generated ${includedStudents.length} ID card${includedStudents.length !== 1 ? 's' : ''}.`);
      }
    } catch (err) {
      console.error('[BulkIdCards] generate error:', err);
      showToast('Failed to generate. Please try again.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  // ── Download a single PDF from the captured images (CR80 54×86mm pages) ──────
  const handleDownloadPdf = () => {
    if (!genImages.length) return;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [54, 86] });
    const B = 0.6; // mm bleed so no white shows after squaring corners
    genImages.forEach((g, i) => {
      if (i > 0) pdf.addPage([54, 86], 'portrait');
      pdf.addImage(g.img, 'PNG', -B, -B, 54 + B * 2, 86 + B * 2);
    });
    pdf.save(`${batchName}.pdf`);
  };

  // ── Download each captured card side as its own PNG ─────────────────────────
  const handleDownloadImages = () => {
    if (!genImages.length) return;
    genImages.forEach(g => {
      const a = document.createElement('a');
      a.href = g.img;
      a.download = `${g.name}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
    showToast(`Downloading ${genImages.length} image${genImages.length !== 1 ? 's' : ''}…`);
  };

  // ── Print all cards — each side on its own CR80 page (native HTML, crisp) ────
  const handlePrintAll = () => {
    const root = captureRootRef.current;
    if (!root || !includedStudents.length) return;

    const wrappers = root.querySelectorAll('[data-bulk-card]');
    const pages = [];
    wrappers.forEach(wrap => {
      [wrap.querySelector('#dcb-idcard-front'), wrap.querySelector('#dcb-idcard-back')]
        .filter(Boolean)
        .forEach(el => {
          const html = el.innerHTML
            .replace(/border-radius\s*:[^;"']+;?/gi, '')
            .replace(/box-shadow\s*:[^;"']+;?/gi, '');
          pages.push(`<div class="idpage">${html}</div>`);
        });
    });
    if (!pages.length) return;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><title>ID Cards</title>
      <base href="${window.location.origin}/">
      <style>
        @page{size:54mm 86mm;margin:0}
        *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}
        html,body{margin:0;padding:0}
        .idpage{width:54mm;height:86mm;overflow:hidden;page-break-after:always}
        .idpage:last-child{page-break-after:auto}
        .idpage>div{width:55.2mm!important;height:87.2mm!important;margin:-0.6mm 0 0 -0.6mm!important;border-radius:0!important}
      </style></head><body>${pages.join('')}</body></html>`
    );
    doc.close();
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
      cleanup();
    }, 500);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const noTemplates = idCardTemplates.length === 0;
  const listReady   = !!cfg && branchIdSet.size > 0;
  const missingPhotos = includedStudents.filter(s => !photoOf(s)).length;
  const missingFields = validate();
  const canGenerate   = missingFields.length === 0 && includedStudents.length > 0;

  // Reusable cells (same markup in section + excel layouts).
  const templateCell = (
    <div>
      <div style={ms.label}><i className="fal fa-id-card" style={{ marginRight: 5 }} />ID Card Template <span style={{ color: '#ef4444' }}>*</span></div>
      <select value={selTemplate ? String(selTemplate.id) : ''} onChange={e => setSelTemplateId(e.target.value)} style={inp} disabled={noTemplates}>
        {noTemplates && <option value="">No ID card template found</option>}
        {idCardTemplates.map(t => <option key={t.id} value={String(t.id)}>{t.template_name}</option>)}
      </select>
    </div>
  );
  const signatureCell = (
    <div>
      <div style={ms.label}><i className="fal fa-signature" style={{ marginRight: 5 }} />Principal Signature <span style={{ color: '#9ca3af', fontWeight: 600 }}>(optional)</span></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1.5px dashed #d1d5db', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', background: principalSig ? '#f0fdf4' : '#fafafa', borderColor: principalSig ? '#86efac' : '#d1d5db', minHeight: 38, boxSizing: 'border-box' }}>
        {principalSig
          ? <img src={principalSig} alt="signature" style={{ maxHeight: 28, maxWidth: 120, objectFit: 'contain' }} />
          : <span style={{ fontSize: 13, color: '#6b7280' }}>✍ Click to upload signature image</span>}
        {principalSig && (
          <button type="button" onClick={e => { e.preventDefault(); setPrincipalSig(''); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>× Remove</button>
        )}
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
          const file = e.target.files[0]; if (!file) return;
          const reader = new FileReader();
          reader.onloadend = () => setPrincipalSig(reader.result);
          reader.readAsDataURL(file);
          e.target.value = '';
        }} />
      </label>
    </div>
  );

  return (
    <div style={{ width: '100%' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, background: '#10b981', color: '#fff', borderRadius: 12, padding: '12px 20px', fontWeight: 600, fontSize: 13, boxShadow: '0 8px 32px #10b98140', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>✓</span> {toast}
        </div>
      )}

      {/* Selection card */}
      <div style={card}>
        <div style={cardHdr}>
          <div style={iconWrap('#c2185b15', '#c2185b')}><i className="fal fa-id-card" /></div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Bulk Student ID Cards</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Generate ID cards for an entire section. Photos load automatically from each student's record.
            </div>
          </div>
          <button type="button" onClick={resetSelection} title="Reset all fields"
            style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7, background: '#fff', color: '#c2185b', border: '1.5px solid #f7c8da', borderRadius: 9, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <i className="fal fa-sync-alt" /> Refresh
          </button>
        </div>

        {/* Filters — all 6 fields in a 3-column grid (2 rows) */}
        <div ref={filterRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 8 }}>
          <div>
            <div style={ms.label}><i className="fal fa-calendar-alt" style={{ marginRight: 5 }} />Academic Year<span style={{ color: '#ef4444' }}> *</span></div>
            <select value={selAcademicYear} onChange={e => { setSelAcademicYear(e.target.value); setExcludedIds(new Set()); }} style={inp}>
              <option value="">-- Select Year --</option>
              {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.academic_year}</option>)}
            </select>
          </div>
          {/* No Branch filter — the header's branch selection scopes these cards. */}
          <MultiSelect
            label="Class" icon="fal fa-layer-group" required
            options={classSelectOpts} selected={selClasses}
            onChange={arr => { setSelClasses(arr); setExcludedIds(new Set()); }}
            onToggleOpen={() => setOpenDropdown(openDropdown === 'class' ? null : 'class')}
            isOpen={openDropdown === 'class'}
          />
          <MultiSelect
            label="Section" icon="fal fa-users" required
            options={sectionSelectOpts} selected={selSections}
            onChange={arr => { setSelSections(arr); setExcludedIds(new Set()); }}
            onToggleOpen={() => setOpenDropdown(openDropdown === 'section' ? null : 'section')}
            isOpen={openDropdown === 'section'}
          />
          {templateCell}
          {signatureCell}
        </div>

        {noTemplates && (
          <div style={{ marginTop: 14, background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '12px 16px', fontSize: 12.5, color: '#92400e' }}>
            <strong>No ID Card template available.</strong> Create one in the Template Builder (certificate type “Student ID Card”) first.
          </div>
        )}
      </div>

      {/* Student list + actions */}
      {listReady && (
        <div style={{ ...card, marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>
              {`${branchName || 'Branch'} · ${selClasses.length} class${selClasses.length !== 1 ? 'es' : ''} · ${selSections.length} section${selSections.length !== 1 ? 's' : ''}`}
              <span style={{ fontWeight: 600, color: '#6b7280', marginLeft: 8 }}>
                {includedStudents.length} of {sourceStudents.length} selected
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type="button" onClick={selectAll}   style={smallBtn}>Select all</button>
              <button type="button" onClick={deselectAll} style={smallBtn}>Clear</button>
            </div>
          </div>

          {sourceStudents.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No students found for this selection.
            </div>
          ) : (
            <>
              {/* Selectable student grid with photo thumbnails */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                {sourceStudents.map(s => {
                  const id = String(s.student_id);
                  const included = !excludedIds.has(id);
                  const photo = photoOf(s);
                  return (
                    <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1.5px solid ${included ? '#c2185b55' : '#e5e7eb'}`, background: included ? '#fdf2f8' : '#fff', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={included} onChange={() => toggleStudent(id)} style={{ width: 15, height: 15, accentColor: '#c2185b', cursor: 'pointer', flexShrink: 0 }} />
                      {photo
                        ? <img src={photo} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb' }} />
                        : <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}><i className="fal fa-user" /></span>}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.student_name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.admission_no || s.roll_no || ''}{!photo ? ' · no photo' : ''}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {missingPhotos > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px' }}>
                  <i className="fal fa-info-circle" />&nbsp; {missingPhotos} selected student{missingPhotos !== 1 ? 's have' : ' has'} no photo — their card{missingPhotos !== 1 ? 's' : ''} will show the placeholder silhouette.
                </div>
              )}

              {/* Actions — Generate + View, then Download/Print once generated */}
              <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <button type="button" onClick={handleGenerate} disabled={busy || !canGenerate}
                  title={!canGenerate && missingFields.length ? `Select: ${missingFields.join(', ')}` : undefined}
                  style={{ background: (busy || !canGenerate) ? '#9ca3af' : 'linear-gradient(135deg,#c2185b,#c2185bcc)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 26px', fontWeight: 700, fontSize: 14, cursor: (busy || !canGenerate) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: (busy || !canGenerate) ? 'none' : '0 4px 14px #c2185b44' }}>
                  {busy
                    ? <><i className="fal fa-spinner fa-spin" /> Generating{progress ? ` ${progress.done}/${progress.total}` : ''}…</>
                    : <><i className="fal fa-bolt" /> Generate ({includedStudents.length})</>}
                </button>
                <button type="button" onClick={() => setShowView(true)} disabled={!includedStudents.length}
                  style={{ background: '#fff', color: '#c2185b', border: '2px solid #c2185b', borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="fal fa-eye" /> View
                </button>
                {generated
                  ? <span style={{ fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}><i className="fal fa-check-circle" />&nbsp; Saved to dashboard</span>
                  : (!canGenerate && missingFields.length > 0 && <span style={{ fontSize: 12, color: '#ef4444' }}>Required: {missingFields.join(', ')}</span>)}
              </div>

              {/* Export actions — revealed after Generate */}
              {generated && (
                <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', paddingTop: 14, borderTop: '1.5px dashed #e2e8f0' }}>
                  <button type="button" onClick={handleDownloadPdf} style={actionBtn('#16a34a')}><i className="fal fa-file-pdf" /> Download PDF</button>
                  <button type="button" onClick={handleDownloadImages} style={actionBtn('#9333ea')}><i className="fal fa-image" /> Download Image</button>
                  <button type="button" onClick={handlePrintAll} style={actionBtn('#0891b2')}><i className="fal fa-print" /> Print</button>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Front &amp; back · CR80 (54×86mm) per card</span>
                </div>
              )}

              {/* Live preview grid (scaled) */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Preview</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, background: '#e8edf5', borderRadius: 10, padding: 14, border: '1px solid #d0d8e8', maxHeight: 480, overflowY: 'auto' }}>
                  {renderedCards.slice(0, 12).map(({ student, html }) => (
                    <div key={student.student_id} style={{ zoom: 0.62, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
                  ))}
                  {renderedCards.length > 12 && (
                    <div style={{ alignSelf: 'center', fontSize: 13, color: '#6b7280', padding: '0 12px' }}>
                      +{renderedCards.length - 12} more — all {renderedCards.length} are included in the download/print.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* View modal — all cards at full size */}
      {showView && (
        <div onClick={() => setShowView(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.6)', display: 'flex', flexDirection: 'column', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 1100, width: '100%', margin: '0 auto', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1.5px solid #e2e8f0' }}>
              <div style={iconWrap('#c2185b15', '#c2185b')}><i className="fal fa-id-card" /></div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
                ID Card Preview <span style={{ fontWeight: 600, color: '#6b7280' }}>· {renderedCards.length} card{renderedCards.length !== 1 ? 's' : ''}</span>
              </div>
              <button type="button" onClick={() => setShowView(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflow: 'auto', padding: 20, background: '#e8edf5' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'center' }}>
                {renderedCards.map(({ student, html }) => (
                  <div key={student.student_id} style={{ flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: html }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden full-size render used for capture (PDF / Print source) */}
      <div ref={captureRootRef} style={{ position: 'absolute', left: '-99999px', top: 0 }} aria-hidden="true">
        {renderedCards.map(({ student, html }) => (
          <div key={student.student_id} data-bulk-card style={{ width: 794, background: '#fff' }} dangerouslySetInnerHTML={{ __html: html }} />
        ))}
      </div>
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────

const card    = { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: '20px 22px' };
const cardHdr = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 };
const lbl     = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 };
const inp     = { width: '100%', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#1f2937', background: '#fff', outline: 'none', boxSizing: 'border-box' };
const smallBtn = { background: '#fff', color: '#6366f1', border: '1.5px solid #c7d2fe', borderRadius: 8, padding: '5px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const actionBtn = (color) => ({ background: `${color}12`, color, border: `1.5px solid ${color}40`, borderRadius: 9, padding: '10px 20px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 });
const iconWrap = (bg, color) => ({ background: bg, color, borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 });

export default BulkIdCards;
